import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  formatDigitalTwinMeasurementValue,
  getMeasurementHistory,
  getMeasurementTypes,
} from "../../../utils/digitalTwinUtils";

const formatDateLabel = (timestamp) => {
  if (!timestamp) return "-";

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
  });
};

const formatFullDateLabel = (timestamp) => {
  if (!timestamp) return "-";

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const MeasurementTrendCard = ({ chartData, isOpen, measurement, onToggle }) => {
  if (!chartData?.length) return null;

  return (
    <Card
      variant="outlined"
      sx={{
        ml: 2,
        mr: 2,
        mt: 1,
        mb: 1,
        pl: 2,
        pr: 2,
        backgroundColor: "background.default",
      }}
    >
      <CardContent>
        <Box
          onClick={onToggle}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onToggle();
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            cursor: "pointer",
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
            Andamento {measurement.label}
          </Typography>
          <Box sx={{ display: "inline-flex", color: "text.secondary" }}>
            {isOpen ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
          </Box>
        </Box>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Box sx={{ pt: 2 }}>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart
                data={chartData}
                margin={{ top: 12, right: 24, bottom: 8, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestampMs"
                  type="number"
                  scale="time"
                  domain={["dataMin", "dataMax"]}
                  tickFormatter={formatDateLabel}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  width={48}
                  domain={["auto", "auto"]}
                />
                <ChartTooltip
                  labelFormatter={formatFullDateLabel}
                  formatter={(value) => [
                    formatDigitalTwinMeasurementValue(value, measurement.key),
                    measurement.label,
                  ]}
                  cursor={{
                    stroke: "var(--color-border-2)",
                  }}
                  contentStyle={{
                    borderColor: "var(--color-border-2)",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#2E7D32"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const MeasurementTrendCards = ({
  measurements,
  measurementTypes,
  openTrendKey,
  onToggle,
}) => {
  const resolvedMeasurementTypes = useMemo(
    () => measurementTypes || getMeasurementTypes(measurements),
    [measurementTypes, measurements],
  );

  const measurementHistoryByType = useMemo(
    () =>
      resolvedMeasurementTypes.reduce((historyMap, measurement) => {
        historyMap[measurement.key] = getMeasurementHistory(
          measurements,
          measurement.key,
        );
        return historyMap;
      }, {}),
    [resolvedMeasurementTypes, measurements],
  );

  return resolvedMeasurementTypes.map((measurement) => (
    <MeasurementTrendCard
      key={measurement.key}
      measurement={measurement}
      chartData={measurementHistoryByType[measurement.key]}
      isOpen={openTrendKey === measurement.key}
      onToggle={() =>
        onToggle((currentKey) =>
          currentKey === measurement.key ? null : measurement.key,
        )
      }
    />
  ));
};

export default MeasurementTrendCards;
